/**
 * AnaesthesiaRecord.js
 * Full anaesthesia record form for surgeries requiring general, regional,
 * spinal, or epidural anaesthesia. Includes pre-induction assessment,
 * induction/maintenance details, vitals timeline, and fluid balance.
 */
import React, { useState, useEffect, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '../../Utils/FormInputs/TextField';
import Label from '../../Utils/FormInputs/Label';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { AutoCompleteUnControlled } from '../../Utils/FormInputs/AutoCompleteInput';
import { ANAESTHESIA_TYPE, ANAESTHESIA_TYPE_LABELS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';
import VitalsTimeline from './VitalsTimeline';

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  preInductionCard: {
    padding: '12px 16px',
    backgroundColor: '#FAFAFA',
    borderRadius: 6,
    border: '1px solid #F0F0F0',
  },
  preInductionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #F5F5F5',
    '&:last-child': { borderBottom: 'none' },
  },
  preInductionLabel: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  preInductionValue: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#212121',
  },
  // Drug rows
  drugHeader: {
    display: 'flex',
    gap: 8,
    padding: '0 36px 0 0',
    marginBottom: 4,
  },
  drugHeaderCell: {
    fontSize: '0.7rem',
    color: '#9E9E9E',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  drugRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  stockText: {
    fontSize: '0.82rem',
    fontWeight: 600,
    textAlign: 'center',
  },
  // Fluid table
  fluidTable: {
    '& .MuiTableCell-root': {
      padding: '8px 12px',
      fontSize: '0.82rem',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#9E9E9E',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      backgroundColor: '#FAFAFA',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTop: '1px solid #EEEEEE',
    marginTop: 8,
  },
  errorText: {
    color: '#F44336',
    fontSize: '0.78rem',
    marginTop: 4,
  },
}));

const AnaesthesiaRecord = ({ surgeryId, patientInfo = {} }) => {
  const classes = useStyles();

  // Anaesthesia type
  const [anaesthesiaType, setAnaesthesiaType] = useState(ANAESTHESIA_TYPE.GENERAL);

  // Pre-induction (read-only from patient)
  const asaClass = patientInfo.asa_class || 'II';
  const airway = patientInfo.airway || 'Mallampati II';
  const allergies = patientInfo.allergies || 'None known';

  // Induction drugs
  const emptyDrugRow = () => ({ drug_name: '', product_id: '', stock: null, qty: '', induction_time: '', technique_notes: '' });
  const [inductionDrugs, setInductionDrugs] = useState([emptyDrugRow()]);
  const [drugSearchOptions, setDrugSearchOptions] = useState([]);
  const drugDebounceRef = useRef(null);
  const [drugRowsKey, setDrugRowsKey] = useState(0);

  // Maintenance
  const [maintenanceAgents, setMaintenanceAgents] = useState('');
  const [ventilationMode, setVentilationMode] = useState('');

  // Vitals
  const [vitalsData, setVitalsData] = useState({ timestamp: '', values: {} });
  const [vitalsInitial, setVitalsInitial] = useState(null);

  // Fluid balance
  const [ivFluids, setIvFluids] = useState([{ id: 1, name: '', volume: '', rate: '' }]);
  const [estimatedBloodLoss, setEstimatedBloodLoss] = useState('');
  const [urineOutput, setUrineOutput] = useState('');

  // Recovery
  const [recoveryNotes, setRecoveryNotes] = useState('');

  // UI
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.getAnaesthesiaRecord(surgeryId)
      .then((res) => {
        const d = res?.data;
        if (!d) return;
        if (d.anaesthesia_type) setAnaesthesiaType(d.anaesthesia_type);
        if (Array.isArray(d.induction_drugs) && d.induction_drugs.length > 0) {
          setInductionDrugs(d.induction_drugs.map((r) => ({
            drug_name: r.drug_name || '', product_id: r.product_id || '',
            stock: null, qty: r.qty || '', induction_time: r.induction_time || '',
            technique_notes: r.technique_notes || '',
          })));
          setDrugRowsKey((k) => k + 1);
        }
        if (d.maintenance_agents) setMaintenanceAgents(d.maintenance_agents);
        if (d.ventilation_mode) setVentilationMode(d.ventilation_mode);
        if (Array.isArray(d.iv_fluids) && d.iv_fluids.length > 0) {
          setIvFluids(d.iv_fluids.map((f, i) => ({ ...f, id: f.id || i + 1 })));
        }
        if (d.estimated_blood_loss != null) setEstimatedBloodLoss(String(d.estimated_blood_loss));
        if (d.urine_output != null) setUrineOutput(String(d.urine_output));
        if (d.recovery_notes) setRecoveryNotes(d.recovery_notes);
        if (d.vitals && (d.vitals.timestamp || Array.isArray(d.vitals.metrics))) {
          setVitalsInitial(d.vitals);
        }
      })
      .catch(() => {});
  }, [surgeryId]);

  // ── Drug handlers ─────────────────────────────────
  const handleDrugSearch = (inputValue) => {
    clearTimeout(drugDebounceRef.current);
    if (!inputValue || inputValue.length < 2) { setDrugSearchOptions([]); return; }
    drugDebounceRef.current = setTimeout(async () => {
      try {
        const res = await surgeryService.searchSurgeryProducts(inputValue);
        const data = res.data || [];
        setDrugSearchOptions(Array.isArray(data) ? data.filter((p) => typeof p === 'object') : []);
      } catch { setDrugSearchOptions([]); }
    }, 400);
  };

  const handleDrugSelect = async (obj, idx) => {
    const name = obj?.innerText || obj?.label || '';
    const productId = obj?.value || '';
    setInductionDrugs((prev) => prev.map((row, i) =>
      i === idx ? { ...row, drug_name: name, product_id: productId, stock: null } : row
    ));
    if (!productId) return;
    try {
      const res = await surgeryService.getProductBatches(productId);
      const batchData = res.data?.product || res.data || [];
      const total = Array.isArray(batchData)
        ? batchData.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0) : 0;
      setInductionDrugs((prev) => prev.map((row, i) =>
        i === idx ? { ...row, stock: total } : row
      ));
    } catch {}
  };

  const handleDrugRowChange = (field, idx) => (e) => {
    const value = e.target.value;
    setInductionDrugs((prev) => prev.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row
    ));
    if (field === 'induction_time' && value) {
      setErrors((prev) => ({ ...prev, inductionTime: undefined }));
    }
  };

  const handleAddInductionRow = () => setInductionDrugs((prev) => [...prev, emptyDrugRow()]);
  const handleRemoveInductionDrug = (idx) => setInductionDrugs((prev) => prev.filter((_, i) => i !== idx));

  // ── Fluid handlers ────────────────────────────────
  const handleAddFluidRow = () => setIvFluids((prev) => [...prev, { id: Date.now(), name: '', volume: '', rate: '' }]);
  const handleFluidChange = (id, field) => (e) => {
    const value = e.target.value;
    setIvFluids((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };
  const handleRemoveFluidRow = (id) => setIvFluids((prev) => prev.filter((row) => row.id !== id));

  // ── Save ──────────────────────────────────────────
  const handleSave = async () => {
    const newErrors = {};
    if (!anaesthesiaType) newErrors.anaesthesiaType = 'Anaesthesia type is required';
    if (inductionDrugs.every((r) => !r.induction_time)) newErrors.inductionTime = 'At least one induction time is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    try {
      await surgeryService.createAnaesthesiaRecord({
        surgery_id: surgeryId,
        anaesthesia_type: anaesthesiaType,
        asa_class: asaClass,
        airway,
        allergies,
        induction_drugs: inductionDrugs,
        maintenance_agents: maintenanceAgents,
        ventilation_mode: ventilationMode,
        vitals: {
          timestamp: vitalsData.timestamp,
          metrics: Object.entries(vitalsData.values)
            .filter(([, v]) => v !== '')
            .map(([metric_id, metric_value]) => ({ metric_id: Number(metric_id), metric_value })),
        },
        iv_fluids: ivFluids.filter((f) => f.name),
        estimated_blood_loss: estimatedBloodLoss ? Number(estimatedBloodLoss) : null,
        urine_output: urineOutput ? Number(urineOutput) : null,
        recovery_notes: recoveryNotes,
      });
      setSnackbar({ open: true, message: 'Anaesthesia record saved successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save anaesthesia record', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>Anaesthesia Record</Typography>
        <Typography className={classes.subtitle}>
          {patientInfo.patient_name || 'Patient'} -- Surgery #{surgeryId}
        </Typography>
      </div>

      {/* Anaesthesia Type */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Anaesthesia Type</Typography>
        <FormControl component="fieldset" error={!!errors.anaesthesiaType}>
          <RadioGroup
            row
            value={anaesthesiaType}
            onChange={(e) => {
              setAnaesthesiaType(e.target.value);
              setErrors((prev) => ({ ...prev, anaesthesiaType: undefined }));
            }}
          >
            {Object.entries(ANAESTHESIA_TYPE_LABELS).map(([key, label]) => (
              <FormControlLabel
                key={key}
                value={key}
                control={<Radio color="primary" size="small" />}
                label={<span style={{ fontSize: '0.82rem' }}>{label}</span>}
              />
            ))}
          </RadioGroup>
          {errors.anaesthesiaType && <Typography className={classes.errorText}>{errors.anaesthesiaType}</Typography>}
        </FormControl>
      </div>

      {/* Pre-Induction Assessment */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Pre-Induction Assessment</Typography>
        <div className={classes.preInductionCard}>
          <div className={classes.preInductionRow}>
            <span className={classes.preInductionLabel}>ASA Class</span>
            <span className={classes.preInductionValue}>{asaClass}</span>
          </div>
          <div className={classes.preInductionRow}>
            <span className={classes.preInductionLabel}>Airway</span>
            <span className={classes.preInductionValue}>{airway}</span>
          </div>
          <div className={classes.preInductionRow}>
            <span className={classes.preInductionLabel}>Allergies</span>
            <span className={classes.preInductionValue}>{allergies}</span>
          </div>
        </div>
      </div>

      {/* Induction Details */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Induction Details</Typography>

        {/* Drug row header */}
        <div className={classes.drugHeader}>
          {[
            { label: 'Drug Name', flex: 2 },
            { label: 'Stock', flex: '0 0 80px' },
            { label: 'Qty', flex: '0 0 70px' },
            { label: 'Time', flex: '0 0 110px' },
            { label: 'Intubation / Notes', flex: 2 },
          ].map((h) => (
            <span key={h.label} className={classes.drugHeaderCell} style={{ flex: h.flex, minWidth: typeof h.flex === 'string' ? parseInt(h.flex.split(' ')[2]) : undefined }}>
              {h.label}
            </span>
          ))}
        </div>

        {inductionDrugs.map((row, idx) => (
          <div key={`${drugRowsKey}-${idx}`} className={classes.drugRow}>
            <div style={{ flex: 2 }}>
              <AutoCompleteUnControlled
                options={drugSearchOptions}
                idx={idx}
                handleChange={handleDrugSearch}
                handleProductData={handleDrugSelect}
                placeholder="Search surgery stock..."
                medicine_value={row.drug_name ? { label: row.drug_name, value: row.product_id } : null}
              />
            </div>
            <div style={{ flex: '0 0 80px', textAlign: 'center' }}>
              <span className={classes.stockText} style={{
                color: row.stock === null ? '#BDBDBD' : row.stock === 0 ? '#F44336' : '#388E3C',
              }}>
                {row.stock === null ? '--' : row.stock}
              </span>
            </div>
            <div style={{ flex: '0 0 70px' }}>
              <TextField
                type="number"
                value={row.qty}
                name={`qty_${idx}`}
                onChange={(e) => {
                  const v = e.target.value;
                  setInductionDrugs((prev) => prev.map((r, i) => i === idx ? { ...r, qty: v } : r));
                }}
              />
            </div>
            <div style={{ flex: '0 0 110px' }}>
              <TextField
                type="time"
                value={row.induction_time}
                name={`time_${idx}`}
                onChange={handleDrugRowChange('induction_time', idx)}
              />
            </div>
            <div style={{ flex: 2 }}>
              <TextField
                type="text"
                value={row.technique_notes}
                name={`notes_${idx}`}
                onChange={handleDrugRowChange('technique_notes', idx)}
                placeholder="LMA, intubation details..."
              />
            </div>
            <IconButton size="small" onClick={() => handleRemoveInductionDrug(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        {errors.inductionTime && <Typography className={classes.errorText}>{errors.inductionTime}</Typography>}

        <button type="button" className="btn btn-sm sms-gray-btn mt-2" onClick={handleAddInductionRow}>
          + Add Drug
        </button>
      </div>

      {/* Maintenance */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Maintenance</Typography>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Agents Used" />
              <TextField
                name="maintenance_agents"
                value={maintenanceAgents}
                onChange={(e) => setMaintenanceAgents(e.target.value)}
                type="text"
                placeholder="e.g. Isoflurane 1.5%, O2/Air"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Ventilation Mode" />
              <TextField
                name="ventilation_mode"
                value={ventilationMode}
                onChange={(e) => setVentilationMode(e.target.value)}
                type="text"
                placeholder="e.g. Volume Controlled"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Vitals Timeline */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Intra-Operative Vitals</Typography>
        <VitalsTimeline onChange={setVitalsData} initialData={vitalsInitial} />
      </div>

      {/* Fluid Balance */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Fluid Balance</Typography>

        <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8, fontSize: '0.82rem', color: '#424242' }}>
          IV Fluids In
        </Typography>
        <TableContainer>
          <Table size="small" className={classes.fluidTable}>
            <TableHead>
              <TableRow>
                <TableCell>Fluid</TableCell>
                <TableCell>Volume (ml)</TableCell>
                <TableCell>Rate (ml/hr)</TableCell>
                <TableCell style={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {ivFluids.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      name={`fluid_name_${row.id}`}
                      value={row.name}
                      onChange={handleFluidChange(row.id, 'name')}
                      type="text"
                      placeholder="e.g. Normal Saline"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name={`fluid_vol_${row.id}`}
                      value={row.volume}
                      onChange={handleFluidChange(row.id, 'volume')}
                      type="number"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name={`fluid_rate_${row.id}`}
                      value={row.rate}
                      onChange={handleFluidChange(row.id, 'rate')}
                      type="number"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleRemoveFluidRow(row.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <button type="button" className="btn btn-sm sms-gray-btn mt-2" onClick={handleAddFluidRow}>
          + Add Fluid
        </button>

        <div style={{ marginTop: 16 }}>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Est. Blood Loss (ml)" />
                <TextField
                  name="estimated_blood_loss"
                  value={estimatedBloodLoss}
                  onChange={(e) => setEstimatedBloodLoss(e.target.value)}
                  type="number"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Urine Output (ml)" />
                <TextField
                  name="urine_output"
                  value={urineOutput}
                  onChange={(e) => setUrineOutput(e.target.value)}
                  type="number"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      {/* Recovery Handover Notes */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Recovery Handover Notes</Typography>
        <Form.Group>
          <ResizableTextarea
            rows={4}
            name="recovery_notes"
            value={recoveryNotes}
            onChange={(e) => setRecoveryNotes(e.target.value)}
            placeholder="Key info for PACU team: airway issues, analgesia plan, fluid orders..."
          />
        </Form.Group>
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <button
          type="button"
          className="btn btn-sm sms-btn px-4"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving...' : 'Save Anaesthesia Record'}
        </button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AnaesthesiaRecord;
